import { SidebarSimpleIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useMatch, useParams } from "react-router";
import { useReadingDetail } from "@/api/reseller/queries";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useSidebar } from "@/components/ui/sidebar";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";

import { getNavItems } from "@/config/navigation";
import { isValidLang, type Lang, ROUTE_SLUGS } from "@/config/routes";
import { useAuth } from "@/contexts/auth-context";
import type { UserRole } from "@/types/auth";
import { getUserRole } from "@/types/auth";

function getInitials(name: string): string {
	return name
		.split(/[\s@.]+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part[0]?.toUpperCase())
		.join("");
}

function DistrictBreadcrumb({
	districtName,
	lang,
}: {
	districtName: string;
	lang: Lang;
}) {
	const { t } = useTranslation();
	return (
		<nav className="flex items-center gap-1.5 text-base whitespace-nowrap">
			<Link
				to={`/${lang}/${ROUTE_SLUGS.districts[lang]}`}
				className="font-medium text-muted-foreground hover:text-foreground transition-colors"
			>
				{t("nav.districts")}
			</Link>
			<span className="text-muted-foreground">/</span>
			<span className="font-medium">{districtName}</span>
		</nav>
	);
}

function MeterBreadcrumb({
	sn,
	lang,
	meterType,
}: {
	sn: string;
	lang: Lang;
	meterType: string | null;
}) {
	const { t } = useTranslation();
	const listUrl =
		meterType === "cieplo"
			? `/${lang}/${ROUTE_SLUGS.heatMeters[lang]}`
			: meterType === "energia"
				? `/${lang}/${ROUTE_SLUGS.electricityMeters[lang]}`
				: `/${lang}/${ROUTE_SLUGS.waterMeters[lang]}`;
	const listLabel =
		meterType === "cieplo"
			? t("nav.heatMeters")
			: meterType === "energia"
				? t("nav.electricityMeters")
				: t("nav.waterMeters");
	return (
		<nav className="flex items-center gap-1.5 text-base whitespace-nowrap">
			<Link
				to={listUrl}
				className="font-medium text-muted-foreground hover:text-foreground transition-colors"
			>
				{listLabel}
			</Link>
			<span className="text-muted-foreground">/</span>
			<span className="font-mono font-medium">{sn}</span>
		</nav>
	);
}

function ReadingDetailBreadcrumb({
	readingId,
	lang,
}: {
	readingId: string;
	lang: Lang;
}) {
	const { t, i18n } = useTranslation();
	const { data } = useReadingDetail(Number(readingId));
	const label = data?.reading_date
		? new Date(data.reading_date).toLocaleString(i18n.language, {
				dateStyle: "medium",
				timeStyle: "short",
			})
		: null;
	return (
		<nav className="flex items-center gap-1.5 text-base whitespace-nowrap">
			<Link
				to={`/${lang}/${ROUTE_SLUGS.readings[lang]}`}
				className="font-medium text-muted-foreground hover:text-foreground transition-colors"
			>
				{t("nav.readings")}
			</Link>
			<span className="text-muted-foreground">/</span>
			<span className="font-medium">{label}</span>
		</nav>
	);
}

function DetectedMetersBreadcrumb({ sn, lang }: { sn: string; lang: Lang }) {
	const { t } = useTranslation();
	return (
		<nav className="flex items-center gap-1.5 text-base whitespace-nowrap">
			<Link
				to={`/${lang}/${ROUTE_SLUGS.mediaTransmitters[lang]}`}
				className="font-medium text-muted-foreground hover:text-foreground transition-colors"
			>
				{t("nav.mediaTransmitters")}
			</Link>
			<span className="text-muted-foreground">/</span>
			<span className="font-mono font-medium">{sn}</span>
		</nav>
	);
}

function MqttSourceBreadcrumb({ sn, lang }: { sn: string; lang: Lang }) {
	const { t } = useTranslation();
	return (
		<nav className="flex items-center gap-1.5 text-base whitespace-nowrap">
			<Link
				to={`/${lang}/${ROUTE_SLUGS.sources[lang]}`}
				className="font-medium text-muted-foreground hover:text-foreground transition-colors"
			>
				{t("nav.sources")}
			</Link>
			<span className="text-muted-foreground">/</span>
			<span className="font-mono font-medium">{sn}</span>
		</nav>
	);
}

function MqttMeterReadingsBreadcrumb({
	sn,
	readingId,
	lang,
}: {
	sn: string;
	readingId: string;
	lang: Lang;
}) {
	const { t } = useTranslation();

	return (
		<nav className="flex items-center gap-1.5 text-base whitespace-nowrap">
			<Link
				to={`/${lang}/${ROUTE_SLUGS.sources[lang]}`}
				className="font-medium text-muted-foreground hover:text-foreground transition-colors"
			>
				{t("nav.sources")}
			</Link>
			<span className="text-muted-foreground">/</span>
			<Link
				to={`/${lang}/${ROUTE_SLUGS.sources[lang]}/${sn}`}
				className="font-mono font-medium text-muted-foreground hover:text-foreground transition-colors"
			>
				{sn}
			</Link>
			<span className="text-muted-foreground">/</span>
			<Link
				to={`/${lang}/${ROUTE_SLUGS.readings[lang]}?search=${sn}`}
				className="font-medium text-muted-foreground hover:text-foreground transition-colors"
			>
				{t("nav.readings")}
			</Link>
			<span className="text-muted-foreground">/</span>
			<span className="font-medium">{readingId}</span>
		</nav>
	);
}

export function SiteHeader() {
	const { pathname } = useLocation();
	const { lang } = useParams<{ lang: string }>();
	const { t } = useTranslation();
	const { toggleSidebar, open } = useSidebar();
	const { user } = useAuth();
	const currentLang: Lang = lang && isValidLang(lang) ? lang : "pl";
	const roleLabels: Record<UserRole, string> = {
		admin: t("roles.admin"),
		district_manager: t("roles.districtManager"),
		reseller_manager: t("roles.resellerManager"),
		reseller: t("roles.reseller"),
		end_user: t("roles.endUser"),
	};
	const role = user ? getUserRole(user) : null;
	const roleLabel = role ? roleLabels[role] : "";

	// All useMatch calls must be unconditional — one per slug variant
	const districtMatchPl = useMatch("/:lang/osiedla/:name");
	const districtMatchEn = useMatch("/:lang/districts/:name");
	const meterMatchPl = useMatch("/:lang/liczniki/:type/:sn");
	const meterMatchEn = useMatch("/:lang/meters/:type/:sn");
	const readingDetailMatchPl = useMatch("/:lang/odczyty/:id");
	const readingDetailMatchEn = useMatch("/:lang/readings/:id");
	const mqttSourceMatchPl = useMatch("/:lang/bramki/:sn");
	const mqttSourceMatchEn = useMatch("/:lang/gateways/:sn");
	const mqttMeterReadingsMatchPl = useMatch(
		"/:lang/bramki/:sn/odczyty/:readingId/liczniki",
	);
	const mqttMeterReadingsMatchEn = useMatch(
		"/:lang/gateways/:sn/readings/:readingId/meters",
	);
	const detectedMetersMatchPl = useMatch(
		"/:lang/nadajniki-mediow/:sn/wykryte-liczniki",
	);
	const detectedMetersMatchEn = useMatch(
		"/:lang/media-transmitters/:sn/detected-meters",
	);

	const districtMatch = districtMatchPl ?? districtMatchEn;
	const meterMatch = meterMatchPl ?? meterMatchEn;
	const readingDetailMatch = readingDetailMatchPl ?? readingDetailMatchEn;
	const mqttSourceMatch = mqttSourceMatchPl ?? mqttSourceMatchEn;
	const mqttMeterReadingsMatch =
		mqttMeterReadingsMatchPl ?? mqttMeterReadingsMatchEn;
	const detectedMetersMatch = detectedMetersMatchPl ?? detectedMetersMatchEn;

	const navItems = getNavItems(t, currentLang);
	const flatNavItems = navItems.flatMap((i) => (i.items ? [...i.items] : [i]));
	const navItem = flatNavItems.find((item) =>
		item.url
			? item.exact
				? pathname === item.url
				: pathname.startsWith(item.url)
			: false,
	);
	const pageTitle = navItem?.title ?? "";

	const districtName = districtMatch
		? decodeURIComponent(districtMatch.params.name ?? "")
		: null;
	const meterSn = meterMatch?.params.sn ?? null;
	const meterType = meterMatch?.params.type ?? null;

	return (
		<header className="relative z-10 flex h-(--header-height) shrink-0 items-center gap-2 border-b bg-background/80 backdrop-blur-sm transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
			<a
				href="#main-content"
				className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-background focus:px-4 focus:py-2 focus:text-foreground focus:ring-2 focus:ring-ring"
			>
				{t("nav.dashboard")}
			</a>
			<div className="flex h-full w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
				<Tooltip>
					<TooltipTrigger asChild>
						<Button
							variant="ghost"
							size="icon-sm"
							className="-ml-1 cursor-pointer"
							onClick={toggleSidebar}
							aria-label={open ? t("sidebar.hide") : t("sidebar.show")}
						>
							<SidebarSimpleIcon aria-hidden="true" />
						</Button>
					</TooltipTrigger>
					<TooltipContent side="bottom">
						{open ? t("sidebar.hide") : t("sidebar.show")}{" "}
						<kbd className="pointer-events-none ml-1.5 rounded-full bg-background/80 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground/70">
							{/(Mac|iPhone|iPod|iPad)/i.test(navigator.userAgent)
								? "⌘+."
								: "Ctrl+."}
						</kbd>
					</TooltipContent>
				</Tooltip>
				<Separator orientation="vertical" className="mx-2 h-5 self-center" />
				{districtName ? (
					<DistrictBreadcrumb districtName={districtName} lang={currentLang} />
				) : meterSn ? (
					<MeterBreadcrumb
						sn={meterSn}
						lang={currentLang}
						meterType={meterType}
					/>
				) : mqttMeterReadingsMatch ? (
					<MqttMeterReadingsBreadcrumb
						sn={mqttMeterReadingsMatch.params.sn ?? ""}
						readingId={mqttMeterReadingsMatch.params.readingId ?? ""}
						lang={currentLang}
					/>
				) : detectedMetersMatch ? (
					<DetectedMetersBreadcrumb
						sn={detectedMetersMatch.params.sn ?? ""}
						lang={currentLang}
					/>
				) : mqttSourceMatch ? (
					<MqttSourceBreadcrumb
						sn={mqttSourceMatch.params.sn ?? ""}
						lang={currentLang}
					/>
				) : readingDetailMatch ? (
					<ReadingDetailBreadcrumb
						readingId={readingDetailMatch.params.id ?? ""}
						lang={currentLang}
					/>
				) : (
					pageTitle && (
						<h1 className="text-base font-medium whitespace-nowrap">
							{pageTitle}
						</h1>
					)
				)}
				<div className="flex-1" />
				{user && (
					<div className="flex items-center gap-2">
						<div className="hidden text-right leading-tight sm:grid">
							<span className="truncate text-xs font-medium">
								{user.username}
							</span>
							<span className="truncate text-[11px] text-muted-foreground">
								{roleLabel}
							</span>
						</div>
						<Avatar className="size-8">
							<AvatarFallback>{getInitials(user.username)}</AvatarFallback>
						</Avatar>
					</div>
				)}
			</div>
		</header>
	);
}
